import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import { TypeBookingAlert, TypeBookingState } from '@widgets/Restaurant/Booking/model/types'
import { TypeExtendedScheduleDay, TypeExtendedScheduleTimeSlot } from '@shared/types/schedule/types'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { isPhonePatternValid } from '@widgets/Restaurant/Booking/lib/helper'
import { generateDatesOptions, generateTimeOptions } from '@shared/lib/helpers/schedule/schedule'
import { fetchRemarkedSlots, initFetchRemarkedSlotsNearTwoDays } from '@widgets/Restaurant/Booking/model/thunks'
import { fetchRestoplaceSlots, initFetchRestoplaceSlotsNearTwoDays } from '@widgets/Restaurant/Booking/model/thunks'

const IS_PROD = typeof window !== 'undefined' && window.document.location.host === 'www.afisha.ru'

const initialState: TypeBookingState = {
  isOpenModal: false,
  isOpenScreenForm: false,
  isOpenScreenConfirm: false,
  isOpenScreenSuccess: false,
  source: '',
  restaurantSource: {
    id: null,
    name: '',
    slug: '',
    schedule: {},
    couponCode: '',
    couponValue: '',
    alerts: null,
    remarked: false,
    restoplace: false,
    booking: {
      afisha: {
        remarked: { active: false },
        restoplace: { active: false },
        alert: { active: false },
      },
    },
    discounts: {
      remarked: null,
      restoplace: null,
      tastyclub: null,
    },
    deposit: {
      active: false,
      text: null,
    },
  },
  form: {
    isErrorsVisible: false,
    guestsCountField: {
      min: 1,
      max: 50,
      value: 2,
      valid: true,
    },
    nameField: { id: 'name', value: '', valid: false },
    phoneField: { id: 'phone', value: '', valid: false },
    commentField: {
      id: 'comment',
      value: !IS_PROD ? 'this is TEST booking from Afisha employee' : '',
      valid: true,
    },
    dateField: {
      loading: true,
      selected: null,
      options: [],
      valid: false,
    },
    timeField: {
      loading: true,
      selected: null,
      options: [],
      valid: false,
    },
    checkDiscountLoading: false,
  },
}

const restaurantBooking = createSlice({
  name: 'booking',
  initialState,

  reducers: {
    closeBooking(state) {
      if (state.isOpenScreenSuccess || state.isOpenScreenConfirm) return initialState
      else {
        state.isOpenScreenConfirm = true
        state.isOpenScreenForm = false
      }
    },

    setIsOpenScreenConfirm(state, action: PayloadAction<boolean>) {
      state.isOpenScreenConfirm = action.payload
      state.isOpenScreenForm = !action.payload
    },

    setIsOpenScreenSuccess(state, action: PayloadAction<boolean>) {
      state.isOpenScreenSuccess = action.payload
      state.isOpenScreenForm = !action.payload
    },

    setRestaurantData(state, action: PayloadAction<TypeMappingRestOutput>) {
      const actionRest = action.payload

      const getAlerts = () => {
        const results: TypeBookingAlert[] = []

        if (actionRest?.booking?.afisha?.alert?.active && actionRest?.booking?.afisha?.alert?.message) {
          results.push({
            id: 1,
            type: 'default',
            visible: true,
            disabled: false,
            color: '#E0EEFF',
            text: actionRest.booking.afisha.alert.message,
          })
        }

        if (actionRest?.discounts?.restoplace || actionRest?.discounts?.remarked) {
          results.push({
            id: 2,
            type: 'discount',
            visible: true,
            disabled: false,
            color: '#D2FF98',
            aside: `-${actionRest.discounts.restoplace || actionRest.discounts.remarked}₽`,
            text: `Скидка на основное меню при первом бронировании на сайте Афиша Рестораны`,
            conclusion: `скидка ${getValueDiscount(
              actionRest.discounts.restoplace || actionRest.discounts.remarked,
            )}₽ при первом бронировании`,
          })
        }

        if (actionRest?.deposit?.active && actionRest?.deposit?.text) {
          results.push({
            id: 3,
            type: 'deposit',
            visible: true,
            disabled: false,
            color: '#E0EEFF',
            text: actionRest.deposit.text,
            conclusion: 'предварительно оплачивается депозит',
          })
        }

        return results
      }

      const getValueDiscount = (string) => {
        try {
          return string ? string.match(/\d+/i)[0] : null
        } catch (e) {
          return null
        }
      }

      state.restaurantSource = {
        id: actionRest.id,
        name: actionRest.name,
        slug: actionRest.url?.slug || '',
        schedule: actionRest.schedule.week,
        couponCode: null,
        couponValue: null,
        alerts: getAlerts(),
        remarked: !!actionRest?.booking?.afisha?.remarked?.active || false,
        restoplace: !!actionRest?.booking?.afisha?.restoplace?.active || false,
        booking: {
          afisha: {
            remarked: { active: !!actionRest?.booking?.afisha?.remarked?.active || false },
            restoplace: { active: !!actionRest?.booking?.afisha?.restoplace?.active || false },
            alert: {
              active: !!actionRest?.booking?.afisha?.alert?.active || false,
              message: actionRest?.booking?.afisha?.alert?.message || '',
            },
          },
        },
        discounts: actionRest.discounts,
        deposit: actionRest.deposit,
      }
    },

    openModal(state) {
      state.isOpenModal = true
      state.isOpenScreenForm = true
    },

    setAlerts(state, action: PayloadAction<TypeBookingAlert[]>) {
      state.restaurantSource.alerts = action.payload
    },

    setDisabledAlertItem(state, action: PayloadAction<{ type: string; disabled: boolean }>) {
      if (!state.restaurantSource.alerts.length) return
      const findAlertIndex = state.restaurantSource.alerts.findIndex((item) => item.type === action.payload.type)

      if (findAlertIndex == -1) return
      state.restaurantSource.alerts[findAlertIndex].disabled = action.payload.disabled
    },

    setCheckDiscountLoading(state, action: PayloadAction<boolean>) {
      state.form.checkDiscountLoading = action.payload
    },

    setNameFieldValue(state, action: PayloadAction<string>) {
      let newValue = action.payload || ''

      if (newValue.indexOf('\r') !== -1) {
        newValue = newValue.replace(/\r?\n|\r/g, '')
      } else if (newValue.indexOf('\n') !== -1) {
        newValue = newValue.replace(/\r?\n|\r/g, '')
      }

      const isNamePatternValid = (nameValue: string, isTyping: boolean) => {
        const thisValue = isTyping ? nameValue : nameValue.trim()
        return !!thisValue && thisValue !== '' && thisValue.length >= 2
      }

      state.form.nameField.value = newValue
      state.form.nameField.valid = isNamePatternValid(newValue, true)
    },

    setPhoneFieldValue(state, action) {
      const newValue = action.payload || ''

      state.form.phoneField.value = newValue
      state.form.phoneField.valid = isPhonePatternValid(newValue)
    },

    setCommentFieldValue(state, action: PayloadAction<string>) {
      state.form.commentField.value = action.payload || ''
      state.form.commentField.valid = true
    },

    setIsErrorsVisible(state) {
      state.form.isErrorsVisible = true
    },

    setGuestsCountValue(state, action: PayloadAction<number>) {
      const newValue = action.payload
      const { min, max } = state.form.guestsCountField
      if (newValue <= max && newValue >= min) {
        state.form.guestsCountField.value = action.payload
        state.form.guestsCountField.valid = true
      }
    },

    setDatesOptions(state, action: PayloadAction<undefined | TypeExtendedScheduleDay[]>) {
      const { schedule } = state.restaurantSource
      if (!action.payload && schedule) {
        state.form.dateField.options = generateDatesOptions(schedule)
      }
      if (action.payload) {
        state.form.dateField.options = action.payload
      }
    },

    setSelectedDate(state, action?: PayloadAction<TypeExtendedScheduleDay>) {
      if (action.payload) {
        state.form.dateField.selected = action.payload
        state.form.dateField.valid = true
      } else if (!action.payload && state.form.dateField.options?.length) {
        // @ts-ignore
        const firstOpenDate = state.form.dateField.options.find((option) => option.isRestOpened) || null
        state.form.dateField.selected = firstOpenDate

        state.form.dateField.valid = true
      } else {
        state.form.dateField.selected = null
        state.form.dateField.valid = false
      }

      state.form.dateField.loading = false
    },

    setTimesOptions(state, action?: PayloadAction<TypeExtendedScheduleDay>) {
      if (action.payload) {
        state.form.timeField.options = generateTimeOptions(action.payload)
      } else {
        state.form.timeField.options = state.form.dateField.selected
          ? generateTimeOptions(state.form.dateField.selected)
          : null
      }

      state.form.timeField.loading = false
    },

    setSelectedTime(state, action?: PayloadAction<TypeExtendedScheduleTimeSlot>) {
      const isTimePatternValid = (timeValue: string | undefined) => {
        if (!timeValue) return false
        else return !!timeValue && timeValue !== ''
      }

      if (!action.payload) {
        state.form.timeField.selected = null
        state.form.timeField.valid = false
      } else {
        state.form.timeField.selected = action.payload
        state.form.timeField.valid = isTimePatternValid(state.form.timeField.options[0]?.value)
      }
    },

    // Special reducer for hydrating the state
    extraReducers: {
      // @ts-ignore
      [HYDRATE]: (state, action) => {
        return {
          ...state,
          ...action.payload.notices,
        }
      },
    },
  },

  extraReducers: (builder) => {
    // * remarked handlers *
    builder.addCase(initFetchRemarkedSlotsNearTwoDays.pending, (state) => {
      state.form.dateField.loading = true
      state.form.dateField.selected = null
      state.form.dateField.valid = false

      state.form.timeField.loading = true
      state.form.timeField.options = null
      state.form.timeField.valid = false
      state.form.timeField.selected = null
    })

    builder.addCase(initFetchRemarkedSlotsNearTwoDays.fulfilled, (state, action) => {
      state.form.dateField.loading = false
      state.form.dateField.options = action.payload.map((data) => data.dateOption)
      state.form.dateField.selected = action.payload[0].dateOption
      state.form.dateField.valid = true

      state.form.timeField.loading = false
      state.form.timeField.options = action.payload[0].timeOptions
    })

    builder.addCase(initFetchRemarkedSlotsNearTwoDays.rejected, (state, action) => {
      state.form.dateField.loading = false
      state.form.timeField.loading = false
    })

    builder.addCase(fetchRemarkedSlots.pending, (state) => {
      state.form.dateField.valid = false

      state.form.timeField.loading = true
      state.form.timeField.options = null
      state.form.timeField.valid = false
      state.form.timeField.selected = null
    })

    builder.addCase(fetchRemarkedSlots.fulfilled, (state, action) => {
      state.form.dateField.loading = false
      const hasNewDateInDateOptions = state.form.dateField.options.find(
        // @ts-ignore
        (date) => date.value === action.payload.date.value,
      )
      // @ts-ignore
      if (!hasNewDateInDateOptions) state.form.dateField.options.push(action.payload.date)
      // @ts-ignore
      state.form.dateField.selected = action.payload.date
      state.form.dateField.valid = true

      state.form.timeField.loading = false
      // @ts-ignore
      state.form.timeField.options = action.payload.slots
    })

    builder.addCase(fetchRemarkedSlots.rejected, (state, action) => {
      state.form.dateField.loading = false
      state.form.timeField.loading = false
    })

    // * restoplace handlers *

    builder.addCase(initFetchRestoplaceSlotsNearTwoDays.pending, (state) => {
      state.form.dateField.loading = true
      state.form.dateField.selected = null
      state.form.dateField.valid = false

      state.form.timeField.loading = true
      state.form.timeField.options = null
      state.form.timeField.valid = false
      state.form.timeField.selected = null
    })

    builder.addCase(initFetchRestoplaceSlotsNearTwoDays.fulfilled, (state, action) => {
      state.form.dateField.loading = false
      if (!action.payload?.length) {
        state.form.dateField.options = []
        state.form.dateField.selected = null
        state.form.dateField.valid = false
        state.form.timeField.loading = false
        state.form.timeField.options = []
        return
      }
      state.form.dateField.options = action.payload.map((data) => data.dateOption)
      state.form.dateField.selected = action.payload[0].dateOption
      state.form.dateField.valid = true

      state.form.timeField.loading = false
      state.form.timeField.options = action.payload[0].timeOptions
    })

    builder.addCase(initFetchRestoplaceSlotsNearTwoDays.rejected, (state, action) => {
      state.form.dateField.loading = false
      state.form.timeField.loading = false
    })

    builder.addCase(fetchRestoplaceSlots.pending, (state) => {
      state.form.dateField.valid = false

      state.form.timeField.loading = true
      state.form.timeField.options = null
      state.form.timeField.valid = false
      state.form.timeField.selected = null
    })

    builder.addCase(fetchRestoplaceSlots.fulfilled, (state, action) => {
      state.form.dateField.loading = false
      if (!action.payload?.date || !action.payload?.slots) {
        state.form.dateField.valid = false
        state.form.timeField.loading = false
        state.form.timeField.options = []
        return
      }
      const hasNewDateInDateOptions = state.form.dateField.options.find(
        // @ts-ignore
        (date) => date.value === action.payload.date.value,
      )
      // @ts-ignore
      if (!hasNewDateInDateOptions) state.form.dateField.options.push(action.payload.date)
      // @ts-ignore
      state.form.dateField.selected = action.payload.date
      state.form.dateField.valid = true

      state.form.timeField.loading = false
      // @ts-ignore
      state.form.timeField.options = action.payload.slots
    })

    builder.addCase(fetchRestoplaceSlots.rejected, (state, action) => {
      state.form.dateField.loading = false
      state.form.timeField.loading = false
    })
  },
})

export const {
  closeBooking,
  setRestaurantData,
  openModal,
  setIsOpenScreenConfirm,
  setIsOpenScreenSuccess,
  setIsErrorsVisible,
  setGuestsCountValue,
  setDatesOptions,
  setTimesOptions,
  setSelectedDate,
  setSelectedTime,
  setNameFieldValue,
  setPhoneFieldValue,
  setCommentFieldValue,
  setAlerts,
  setDisabledAlertItem,
  setCheckDiscountLoading,
} = restaurantBooking.actions

export default restaurantBooking.reducer
